import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ordresApi } from "@services/api/ordres";
import { useAtelierStore } from "@store/atelierStore";
import type { Ordre } from "types/ordre";

const PAGE_SIZE = 25;

/** Get a single ordre by ID from the store */
export function getOrdreById(id: string): Ordre | undefined {
  return useOrdreStore.getState().ordres.find((o) => o.id === id);
}

/** Find an ordre matching an appointment/job by plate */
export function findOrdreForPlate(plate: string): Ordre | undefined {
  return useOrdreStore.getState().ordres.find((o) => o.immatriculation === plate);
}

/** Build a default Ordre from an appointment or job for display */
export function buildOrdreFromAppointment(data: { clientName: string; plate: string; id: string }): Ordre {
  return {
    id: `ord-tmp-${data.id}`,
    org_id: "org1",
    num: `TEMP-${data.id.slice(0, 6).toUpperCase()}`,
    name: data.clientName,
    tags: [],
    status: "reception",
    immatriculation: data.plate,
    created_at: { seconds: Math.floor(Date.now() / 1000) },
    interventions: [],
    documents: [],
  };
}

interface OrdreStore {
  ordres: Ordre[];
  totalCount: number;
  isLoading: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  hasPrev: boolean;
  page: number;
  statusFilter: string;
  sortBy: string;
  sortDir: string;
  myOnly: boolean;
  error: string | null;
  _fetchedOrgId: string | undefined;
  _assignedTo: string | undefined;

  fetchOrdres: (orgId: string, status?: string, assignedTo?: string) => Promise<void>;
  fetchNextPage: (orgId: string) => Promise<void>;
  fetchPrevPage: (orgId: string) => Promise<void>;
  goToPage: (orgId: string, page: number) => Promise<void>;
  setSort: (orgId: string, sortBy: string, sortDir: string) => Promise<void>;
  setMyOnly: (myOnly: boolean) => void;
  addOrdre: (ordre: Ordre) => void;
  updateOrdre: (ordre: Ordre) => void;
  removeOrdres: (ids: string[]) => void;
}

export const useOrdreStore = create<OrdreStore>()(
  persist(
    (set) => ({
  ordres: [],
  totalCount: 0,
  isLoading: false,
  isRefreshing: false,
  hasMore: false,
  hasPrev: false,
  page: 0,
  statusFilter: "",
  sortBy: "created_at",
  sortDir: "desc",
  myOnly: false,
  error: null,
  _fetchedOrgId: undefined,
  _assignedTo: undefined,

  fetchOrdres: async (orgId: string, status?: string, assignedTo?: string) => {
    const normalizedStatus = status === "all" ? undefined : status;
    const state = useOrdreStore.getState();
    // Skip if an identical fetch is already in progress
    if (state.isRefreshing && state._fetchedOrgId === orgId && state._assignedTo === assignedTo && state.statusFilter === (normalizedStatus ?? "")) return;
    const showLoading = state.ordres.length === 0;
    if (state.sortBy === "tag_ids") {
      set({ sortBy: "created_at", sortDir: "desc" });
    }
    set({ isLoading: showLoading, isRefreshing: true, error: null, page: 0, _fetchedOrgId: orgId, _assignedTo: assignedTo, statusFilter: normalizedStatus ?? "" });
    try {
      const { sortBy, sortDir } = useOrdreStore.getState();
      const { items, total } = await ordresApi.list({
        org_id: orgId,
        status: normalizedStatus,
        assigned_to: assignedTo,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({
        ordres: items,
        totalCount: total,
        isLoading: false,
        isRefreshing: false,
        page: 1,
        hasMore: items.length === PAGE_SIZE,
        hasPrev: false,
      });
    } catch (error: any) {
      console.error("Error fetching ordres:", error);
      set({ ordres: [], totalCount: 0, isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, error: "Échec du chargement des ordres" });
    }
  },

  fetchNextPage: async (orgId: string) => {
    const { isLoading, hasMore, page, statusFilter, sortBy, sortDir, _assignedTo } = useOrdreStore.getState();
    if (isLoading || !hasMore) return;
    set({ isLoading: true });
    try {
      const { items, total } = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        assigned_to: _assignedTo || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        ordres: items,
        totalCount: total,
        isLoading: false,
        page: state.page + 1,
        hasMore: items.length === PAGE_SIZE,
        hasPrev: true,
      }));
    } catch (error: any) {
      console.error("Error fetching next page:", error);
      set({ isLoading: false });
    }
  },

  fetchPrevPage: async (orgId: string) => {
    const { isLoading, hasPrev, page, statusFilter, sortBy, sortDir, _assignedTo } = useOrdreStore.getState();
    if (isLoading || !hasPrev) return;
    const prevPage = page - 2;
    set({ isLoading: true });
    try {
      const { items, total } = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        assigned_to: _assignedTo || undefined,
        limit: PAGE_SIZE,
        offset: prevPage * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set((state) => ({
        ordres: items,
        totalCount: total,
        isLoading: false,
        page: state.page - 1,
        hasMore: true,
        hasPrev: prevPage > 0,
      }));
    } catch (error: any) {
      console.error("Error fetching prev page:", error);
      set({ isLoading: false });
    }
  },

  goToPage: async (orgId: string, targetPage: number) => {
    const { isLoading, statusFilter, sortBy, sortDir, _assignedTo } = useOrdreStore.getState();
    if (isLoading) return;
    set({ isLoading: true });
    try {
      const { items, total } = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        assigned_to: _assignedTo || undefined,
        limit: PAGE_SIZE,
        offset: (targetPage - 1) * PAGE_SIZE,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({
        ordres: items,
        totalCount: total,
        isLoading: false,
        page: targetPage,
        hasMore: items.length === PAGE_SIZE,
        hasPrev: targetPage > 1,
      });
    } catch (error: any) {
      set({ isLoading: false });
    }
  },

  setSort: async (orgId: string, sortBy: string, sortDir: string) => {
    if (sortBy === "tag_ids") return;
    const { statusFilter } = useOrdreStore.getState();
    set({ sortBy, sortDir, _fetchedOrgId: undefined, isLoading: true, page: 0, hasPrev: false });
    try {
      const { items, total } = await ordresApi.list({
        org_id: orgId,
        status: statusFilter || undefined,
        limit: PAGE_SIZE,
        offset: 0,
        sort_by: sortBy || undefined,
        sort_dir: sortDir,
      });
      set({ ordres: items, totalCount: total, isLoading: false, page: 1, hasMore: items.length === PAGE_SIZE, hasPrev: false });
    } catch (error: any) {
      set({ isLoading: false });
    }
  },

  setMyOnly: (myOnly: boolean) => {
    set({ myOnly, _fetchedOrgId: undefined });
  },

  addOrdre: (ordre: Ordre) => {
    set((state) => ({ ordres: [ordre, ...state.ordres], _fetchedOrgId: undefined }));
  },

  updateOrdre: (ordre: Ordre) => {
    set((state) => {
      const exists = state.ordres.some((o) => o.id === ordre.id);
      return {
        ordres: exists
          ? state.ordres.map((o) => (o.id === ordre.id ? ordre : o))
          : [ordre, ...state.ordres],
      };
    });
  },

  removeOrdres: (ids: string[]) => {
    set((state) => ({
      ordres: state.ordres.filter((o) => !ids.includes(o.id)),
    }));
    const deletePromises = ids.map((id) =>
      ordresApi.delete(id).catch((error) => {
        console.error("Error deleting ordre from API:", error);
      })
    );
    Promise.all(deletePromises).then(() => {
      const atelierState = useAtelierStore.getState();
      if (atelierState._fetchedOrgId) {
        atelierState.fetchData(atelierState._fetchedOrgId, true);
      }
    });
  },
    }),
    {
      name: "ordre-prefs",
      partialize: (state) => ({
        sortBy: state.sortBy,
        sortDir: state.sortDir,
        myOnly: state.myOnly,
      }),
    },
  ),
);
